import { Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ClientProviders, CustomerSession } from './ClientProviders';
import CheckoutPage from './pages/CheckoutPage';
import CheckPayPage from './pages/CheckPayPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import ReceiptViewPage from './pages/ReceiptViewPage';

/**
 * Checkout App Routes
 *
 * IMPORTANT: This app does NOT create users or anonymous auth.
 * Customers MUST arrive with a valid session token from the ordering app;
 * CustomerSession (CustomerAuthHandler) handles the token exchange for every
 * customer route. TWO routes sit outside it — the pay-at-table page, which
 * holds a signed check access token and cannot complete the Firebase sign-in
 * that gate performs, and the digital receipt, which holds a signed print view
 * token and nothing else.
 *
 * The POS keyed-entry page (`/embedded/keyed-entry`) is NOT served by this app
 * at all: `vercel.json` sends it to the lean `embedded.html` entry
 * (src/embedded.tsx). It is a POS surface inside a WebView that can never pass
 * App Check, and every customer behaviour this app carries — on 2026-09-27, a
 * reload on App Check refusal — lands on it if it lives here. Do not add it
 * back; scripts/ops/guards/check-embedded-entry.mjs refuses it.
 */
function AppContent() {
  return (
    <Router>
      <Routes>
        {/* Pay at table (Guest QR Platform piece 3, Ruling R57). A guest scans
            the QR on their table — or on a printed pre-bill — and arrives COLD:
            no cart, no ordering-app hand-off, no session token. The signed
            check access token in the path is the entire credential, and every
            route it calls is public and App-Check-skipped for that reason.
            Mounting this inside CustomerSession blocks the page before it
            renders, on a guest who has everything they need to pay. Three
            segments, so it never collides with the `/:slug` catch-all below.
            `checkPayRouting.test.tsx` fails if it moves inside. */}
        <Route path="/:slug/check/:checkToken" element={<CheckPayPage />} />
        {/* Digital receipt: one composed print drawn as paper, opened from the
            link or QR a till shows when it has no printer (or alongside the
            paper). Cold like the check page — the signed print view token is
            the whole credential — so it sits outside CustomerSession too. Two
            segments with a literal first one, so it never collides with
            `/:slug` (one segment) or the three-segment routes. */}
        <Route path="/receipt/:token" element={<ReceiptViewPage />} />
        <Route
          element={
            <CustomerSession>
              <Outlet />
            </CustomerSession>
          }
        >
          <Route path="/:slug" element={<CheckoutPage />} />
          <Route path="/:slug/confirmation/:orderGuid" element={<OrderConfirmationPage />} />
          {/* <Route path="/:slug/success" element={<SuccessPage />} /> */}
          {/* A path no page claims still meets the gate, which is what tells a
              visitor to come from the restaurant's menu. */}
          <Route path="*" element={null} />
        </Route>
      </Routes>
    </Router>
  );
}

function App() {
  // ClientProviders handles Firebase initialization and auth subscription
  return (
    <ClientProviders>
      <AppContent />
    </ClientProviders>
  );
}

export default App;
